import type {
  Company,
  DomainConvertObject,
  Field,
  LayerVelocityModelStats,
  MapFetchingResult,
  MapId,
  MapInfo,
  Marker,
  MarkerCreationStats,
  ResultCreateVelocityModel,
  ResultLayerVelocityModel,
  VelocityModel,
  WellListItem,
} from '@core/core/types'
import type { ProjectId } from 'common/types'

import type {
  CompanyInfo as ApiCompanyInfo,
  ElementConvertInfo as ApiElementConvertInfo,
  FieldInfo as ApiFieldInfo,
  GridFittingParams as ApiGridFittingParams,
  GridFittingResult as ApiGridFittingResult,
  GridHeader as ApiGridHeader,
  LayerVelocityModel as ApiLayerVelocityModel,
  LayerVelocityModelStats as ApiLayerVelocityModelStats,
  MarkerCreationResult,
  MarkerInfo as ApiMarkerInfo,
  VelocityModelHeader as ApiVelocityModelHeader,
  VelocityModelInfo as ApiVelocityModel,
  WellAndFieldName,
} from '../__generated__/data-contracts'

export function convertToCompany(from: ApiCompanyInfo): Company {
  return {
    id: from.id,
    name: from.name,
    longName: from.longName ?? undefined,
  }
}

export function convertToField(from: ApiFieldInfo): Field {
  return {
    id: from.id,
    name: from.name,
  }
}

export function convertToMarker(from: ApiMarkerInfo): Marker {
  return {
    id: from.uid,
    wellId: from.wellId,
    markerName: from.markerName,
    depth: from.depth,
    depthAbs: from.depthAbs ?? undefined,
    markerType: from.markerType ?? undefined,
  }
}

export function convertToWellListItem(from: WellAndFieldName): WellListItem {
  return {
    id: from.wellId,
    wellName: from.wellName,
    fieldName: from.fieldName,
  }
}

export function getCreationStats(creationResults: MarkerCreationResult[]): MarkerCreationStats {
  const stats: MarkerCreationStats = {
    created: 0,
    skipped: 0,
    updated: 0,
  }
  return creationResults.reduce((stats, result) => {
    switch (result.status) {
      case 'Skipped':
        stats.skipped++
        break
      case 'Created':
        stats.created++
        break
      case 'Updated':
        stats.updated++
        break
    }
    return stats
  }, stats)
}

export function convertToMapInfo(from: ApiGridHeader): MapInfo {
  return {
    id: from.id,
    name: from.name,
    measureUnit: from.name,
    offsetX: from.offsetX,
    offsetY: from.offsetY,
    stepX: from.stepX,
    stepY: from.stepY,
    rotationAngle: from.rotationAngle,
    rotationX: from.rotationX,
    rotationY: from.rotationY,
  }
}

export const convertToGridFittingParams = (
  projectId: ProjectId,
  mapId: MapId,
  markerName: string,
  newMapName: string,
): ApiGridFittingParams => {
  return { projectId, gridId: mapId, createdGridName: newMapName, markerName }
}

export const convertToMapFittingResult = (
  gridFitting: ApiGridFittingResult,
  projectId: ProjectId,
): MapFetchingResult => ({
  projectId,
  mapId: gridFitting.gridId,
  gridAttributeId: gridFitting.gridAttributeId,
  before: gridFitting.firstStepStat,
  after: gridFitting.secondStepStat,
})

export function convertToElementConvertInfo(
  from: ApiElementConvertInfo,
): Omit<DomainConvertObject, 'type'> {
  return {
    id: from.id,
    note: from.note,
    status: from.elementConvertStatus,
  }
}

export function convertToResultCreateVelocityModel(
  projectId: ProjectId,
  from: ApiVelocityModel,
): ResultCreateVelocityModel {
  return {
    id: from.id,
    projectId,
    name: from.name,
    status: from.status,
    note: from.note,
    borderVelocityTop: from.borderVelocityTop,
    layers: from.layers.map(convertToLayerVelocityModel),
    borderVelocityBot: from.borderVelocityBot,
    geometry: { ...from.geometry, stepX: from.geometry.rangeX, stepY: from.geometry.rangeY },
  }
}

function convertToLayerVelocityModel(from: ApiLayerVelocityModel): ResultLayerVelocityModel {
  return {
    gridId: from.gridId,
    key: from.key,
    markerName: from.markerName,
    note: from.note,
    status: from.status,
    stats: convertToLayerVelocityModelStats(from.stats),
  }
}

function convertToLayerVelocityModelStats(
  from: ApiLayerVelocityModelStats,
): LayerVelocityModelStats {
  return {
    a: from.a,
    h0: from.h0,
    r2: from.r2,
    stdDeviation: from.stdDeviation,
    vMax: from.vMax,
    vMin: from.vMin,
  }
}

export function convertToVelocityModelHeader(from: ApiVelocityModelHeader): VelocityModel {
  return {
    name: from.name,
    id: from.uid,
    xMax: from.xMax,
    xMin: from.xMin,
    yMax: from.yMax,
    yMin: from.yMin,
  }
}
